import {
  AnimalScreeningReportVendors,
  VerificationNames,
  VerificationStatus
} from "shared/types";
import { ApplicantOrInvitee, Verification } from "../../types";
import { Cell } from "views/applications-id/components/ApplicantsTable/components/Cell";
import { ReportStatusBadge } from "../ReportStatusBadge";
import { useQuery } from "@tanstack/react-query";
import { APPROVED_PET_STATUSES } from "views/applications-id-report/slices/PetScreeningReportSlice/constants";
import { useMemo } from "react";
import { getVerificationQueryKeys } from "views/applications-id-report/utils/getVerificationQueryKeys";
import api from "api/landlord/applications";
import {
  AnimalScreeningRequestState,
  getAnimalScreeningRawResponseAnimals,
  getAnimalScreeningRequestState,
  getVerificationStatusByVendor,
  hasCompletedAnimalScreeningReport
} from "views/applications-id-report/slices/PetScreeningReportSlice/utils";
import { VerificationReport } from "types/landlord/reports";
import { PetScreeningReport } from "views/applications-id-report/slices/PetScreeningReportSlice/types";
import { AxiosError } from "axios";
import { useAuthContext } from "providers/Auth";
import { EmptyCellIcon } from "./components/EmptyCellIcon";

interface PetScreeningReportSectionProps {
  verification: Verification;
  applicant: ApplicantOrInvitee;
}

export const PetScreeningReportSection = ({
  verification,
  applicant
}: PetScreeningReportSectionProps) => {
  const { permissions } = useAuthContext();
  const { id: verificationId } = verification;

  const query = useQuery<VerificationReport<PetScreeningReport>, AxiosError>({
    queryKey: getVerificationQueryKeys({
      reportName: VerificationNames.PetScreeningReport,
      applicationId: applicant.application_id || 0,
      applicantId: applicant.id || 0,
      verificationId
    }),
    queryFn: () =>
      api
        .getApplicantVerification(
          applicant.application_id || 0,
          applicant.id || 0,
          verificationId
        )
        .then((data) => data.verification)
  });

  const { data: verificationData, isLoading, isError } = query;
  const {
    report,
    requested_at: emailSentAt,
    status,
    vendor
  } = verificationData || {};

  const pets =
    report?.pets ?? getAnimalScreeningRawResponseAnimals(verificationData);
  const numPets = pets?.length || 0;
  const hasCompletedReport =
    hasCompletedAnimalScreeningReport(verificationData);

  const numVerifiedPets =
    (vendor &&
      numPets &&
      pets?.filter((pet) =>
        APPROVED_PET_STATUSES.includes(
          getVerificationStatusByVendor(
            pet,
            vendor as AnimalScreeningReportVendors
          )
        )
      ).length) ||
    0;

  const hasReportData = Boolean(report && Object.keys(report).length > 0);

  const getVerificationStatus = (): VerificationStatus | null => {
    // Nothing known yet: an unbadged cell beats guessing "Not started" at a report
    // that may well be finished.
    if (isLoading || isError) return null;

    if (
      hasCompletedReport ||
      numVerifiedPets ||
      status === VerificationStatus.Completed
    )
      return VerificationStatus.Finished;

    if (emailSentAt && verification.status === VerificationStatus.Pending)
      return VerificationStatus.EmailSent;

    /*
     * BuildReport::PetScreeningReport returns {} until the vendor responds, and an
     * empty object is truthy -- a bare `if (report)` matches every verification whose
     * invite has not come back yet and makes everything below it unreachable.
     */
    if (hasReportData) return VerificationStatus.Processing;

    /*
     * No report and no invite out yet. Left unbadged this cell reads as work in
     * flight, which is how declined applications ended up looking stuck forever.
     * Whether the invite is still coming or never will is settled on the backend
     * (Reject skips it), so the verification status alone decides here -- every
     * waiting case renders the same "Not started" badge either way.
     */
    const requestState = getAnimalScreeningRequestState({
      verificationStatus: status ?? verification.status,
      requestedAt: emailSentAt
    });

    if (requestState === AnimalScreeningRequestState.NotNeeded)
      return VerificationStatus.Skipped;

    // Called off on a decline. Without this the cell falls through to "Not started",
    // which reads as work still to come on an application that is already decided.
    if (requestState === AnimalScreeningRequestState.Canceled)
      return VerificationStatus.Canceled;

    if (requestState === AnimalScreeningRequestState.Requested) {
      /*
       * Only claim an email while the invite is still outstanding. An outcome can
       * arrive without any invite -- the OurPetPolicy sync and the PetScreening
       * webhook both move the status directly -- but it can equally land after one
       * did go out, and there "Email sent" hides a failure behind a cell that reads
       * as still waiting on the applicant.
       */
      const awaitingAnswers =
        !status ||
        status === VerificationStatus.Pending ||
        status === VerificationStatus.Started;

      if (emailSentAt && awaitingAnswers) return VerificationStatus.EmailSent;

      return status ?? null;
    }

    return VerificationStatus.Pending;
  };
  const verificationStatus = getVerificationStatus();

  const numPetsText = useMemo(() => {
    // Same empty-object trap as above: "0" would assert that nothing was found,
    // when on these rows nothing was ever asked for.
    if (!hasReportData) return <EmptyCellIcon />;
    if (!numPets) return "0";
    return `${numVerifiedPets} of ${numPets}`;
  }, [hasReportData, numPets, numVerifiedPets]);

  return (
    <Cell
      isDataCell
      className="relative"
      isPermitted={permissions.canAccessReports.animalScreening}
      showRestrictedFlag={!permissions.canAccessReports.animalScreening}
    >
      {numPetsText}
      {verificationStatus && (
        <ReportStatusBadge overrideStatus={verificationStatus} />
      )}
    </Cell>
  );
};
